import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchUsers from "../../../utils/database/SearchUsers";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const sameUser = (a, b) => {
  if (!a || !b) return false;
  if (a.id && b.id && a.id === b.id) return true;
  if (a.email && b.email && a.email.toLowerCase() === b.email.toLowerCase()) return true;
  return false;
};

const UserSearchPicker = ({
  label = "Add people",
  placeholder = "Search by name or email",
  helperText = "",
  selectedUsers = [],
  onChange,
  excludeUserId = "",
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await SearchUsers(trimmed, {
        excludeId: excludeUserId,
        limit: 10,
        signal: controller.signal,
      });
      if (res.aborted) return;
      setLoading(false);
      if (!res.success) {
        setError(res.error || "Search failed");
        setResults([]);
        return;
      }
      const filtered = res.users.filter(
        (u) => !selectedUsers.some((sel) => sameUser(sel, u))
      );
      setResults(filtered);
      setOpen(true);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, selectedUsers, excludeUserId]);

  const handleSelect = (user) => {
    if (selectedUsers.some((sel) => sameUser(sel, user))) return;
    onChange([...selectedUsers, user]);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const handleRemove = (user) => {
    onChange(selectedUsers.filter((sel) => !sameUser(sel, user)));
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", width: "100%" }}>
        {label && (
          <Typography variant="subtitle2" sx={{ color: "#333366", fontWeight: "bold", mb: 0.5 }}>
            {label}
          </Typography>
        )}
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          helperText={error || helperText}
          error={Boolean(error)}
          InputProps={{
            startAdornment: <PersonAddIcon sx={{ mr: 1, color: "#888" }} fontSize="small" />,
            endAdornment: loading ? <CircularProgress size={16} /> : null,
          }}
        />

        {open && results.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 1300,
              maxHeight: 240,
              overflowY: "auto",
            }}
          >
            <List dense disablePadding>
              {results.map((user) => (
                <ListItemButton
                  key={user.id || user.email}
                  onClick={() => handleSelect(user)}
                >
                  <ListItemText
                    primary={user.displayName || user.email}
                    secondary={user.email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {open && !loading && !error && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0 && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 1300,
              p: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No users found
            </Typography>
          </Paper>
        )}

        {selectedUsers.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {selectedUsers.map((user) => (
              <Chip
                key={user.id || user.email}
                label={user.displayName || user.email}
                onDelete={() => handleRemove(user)}
                deleteIcon={<CloseIcon />}
                size="small"
                sx={{ bgcolor: "#e6f9e6", color: "#333366" }}
              />
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default UserSearchPicker;
